/**
 * Game Mode — mode-specific state creation and helpers.
 */

import type { GameMode, ModeState } from '../types';

/** Create a fresh ModeState for the given mode. */
export function createModeState(mode: GameMode): ModeState {
  return {
    mode,
    dummyX: 0,
    dummyY: 0,
    dummyDamage: 0,
    labCursor: 0,
    labDummyDamage: 0,
    labWindOverride: null,
    labLastHit: null,
    targets: [],
    shotsFired: 0,
    elapsedTime: 0,
    allTargetsHit: false,
    introStep: 0,
    introTimer: 0,
    exitGateX: 0,
    exitGateY: 0,
    exitGateActive: false,
    runComplete: false,
    bestTimes: [],
    // Roguelite
    rogueliteZone: 1,
    rogueliteCheckpointsReached: [],
    rogueliteShot1: null,
    rogueliteShot2: null,
    rogueliteShot1Base: null,
    rogueliteShot2Base: null,
    rogueliteShot1Mods: [],
    rogueliteShot2Mods: [],
    rogueliteHasGrapple: false,
    rogueliteGrappleBonus: 0,
    rogueliteMomentumBonus: 0,
    rogueliteChargeRateMult: 1.0,
    roguelitePityCounter: 0,
    rogueliteCardDrawOptions: null,
    rogueliteCardDrawType: null,
    rogueliteCardDrawCursor: 0,
    rogueliteVoidPauseTimer: 0,
    rogueliteCheckpointYs: [],
    rogueliteEnemyCount: 0,
    // Tutorial
    tutorialStep: 0,
    tutorialStepFrame: 0,
    tutorialComplete: false,
    tutorialSuccessHold: 0,
    tutorialUnlockedKeys: [],
    tutorialVariant: 'offense',
    tutorialEdgeguardFreeze: false,
    tutorialOpponentFade: 1,
    // Survival
    survivalKills: 0,
    survivalSpawnTimer: 0,
    survivalHighScore: null,
  };
}

/** Check if the current mode is Roguelite (ascent superset with cards/enemies). */
export function modeIsRoguelite(mode: GameMode): boolean {
  return mode === 'roguelite';
}

/** Check if the current mode is Ascent (vertical survival). Roguelite is a superset. */
export function modeIsAscent(mode: GameMode): boolean {
  return mode === 'ascent' || mode === 'roguelite';
}

/** Check if the current mode is Classic Artillery (HP damage, no reactive phases). */
export function modeIsClassic(mode: GameMode): boolean {
  return mode === 'classic';
}

/** Check if the current mode uses HP pools instead of damage %. */
export function modeUsesHP(mode: GameMode): boolean {
  return mode === 'classic';
}

/** Check if the current mode is Survival (1P endless enemy waves). */
export function modeIsSurvival(mode: GameMode): boolean {
  return mode === 'survival';
}

/** Check if the current mode is a solo mode (single player during menu/tank select). */
export function isSoloMode(mode: GameMode): boolean {
  return mode === 'training' || mode === 'target_blast' || mode === 'roguelite' || mode === 'survival' || mode === 'tutorial';
}

/** Check if the current mode uses the Cosmic Lottery weapon selection. */
export function modeUsesLottery(mode: GameMode): boolean {
  return mode === 'cosmic_lottery';
}

/** Check if the current mode uses the stock/respawn system. */
export function modeUsesStocks(mode: GameMode): boolean {
  return mode === 'vs_local' || mode === 'vs_online';
}

/** Check if the current mode uses shot tiers (vs modes + classic + training + tutorial + survival). */
export function modeUsesShotTiers(mode: GameMode): boolean {
  return mode === 'vs_local' || mode === 'vs_online' || mode === 'classic' || mode === 'training' || mode === 'survival' || mode === 'tutorial';
}
